import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNotesAction } from "../../../../../store/actions/NotesAction";
import { Button } from "react-bootstrap";
import { formatDate } from "../../../../../utils/dateUtils";
import AddNotes from "./AddNotes.Component";
import PropTypes from "prop-types";

export const NotesHistory = ({ casePaperData }) => {
  const dispatch = useDispatch();
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState(null);

  // Check if in case paper mode
  const isCasePaperMode = Boolean(casePaperData);

  const patientId = useSelector(
    (state) => state.patients?.patientDetails?.patient_id
  );

  const notesFromStore = useSelector((state) => state.notes?.notes || []);

  // Use case paper data if available, otherwise use store data
  let notes = [];
  if (isCasePaperMode) {
    // Get notes from casePaperData.Notes (matches API response structure)
    notes = casePaperData.Notes || [];
  } else {
    notes = notesFromStore;
  }

  const [expandedCategories, setExpandedCategories] = useState({});
  const [showMoreVisible, setShowMoreVisible] = useState({});

  const containerRefs = useRef({});
  const defaultHeight = 150;

  useEffect(() => {
    // Only fetch if NOT in case paper mode and patientId exists
    if (!isCasePaperMode && patientId) {
      dispatch(getNotesAction(patientId));
    }
  }, [dispatch, patientId, isCasePaperMode]);

  /**
   * 🔑 CATEGORIES - Create categories from notes
   * If category_details exists use it, otherwise create a "General" category
   */
  const categories = useMemo(() => {
    const map = {};

    if (!notes || notes.length === 0) {
      return map;
    }

    notes.forEach((note) => {
      // Try to get category from category_details or create a default category based on note_category
      let cat = note.category_details;
      let categoryId = note.note_category;
      let categoryName = null;
      let bgColor = "#f8f9fa";
      let textColor = "#000000";

      if (cat && cat.category_text) {
        // Use existing category details
        categoryName = cat.category_text;
        bgColor = cat.bg_color || "#f8f9fa";
        textColor = cat.forground_color || "#000000";
        categoryId = cat.category_id;
      } else if (categoryId) {
        // If only note_category exists, create a category name
        // Map common category IDs to names
        const categoryMap = {
          "1": "Doctor Notes",
          "2": "Service Notes",
          "3": "General Notes",
          "4": "Telecaller Notes",
          "5": "Important Notes",
        };
        categoryName = categoryMap[categoryId] || `Category ${categoryId}`;
      } else {
        // Default category for notes without any category
        categoryName = "General Notes";
        categoryId = "0";
      }

      if (categoryName && !map[categoryName]) {
        map[categoryName] = {
          id: Number(categoryId),
          bg: bgColor,
          textColor: textColor,
          btn: "primary",
        };
      }
    });

    return map;
  }, [notes]);

  // Height measurement logic
  useEffect(() => {
    setTimeout(() => {
      Object.keys(categories).forEach((category) => {
        const el = containerRefs.current[category];
        if (el) {
          el.style.maxHeight = "none";
          const actualHeight = el.scrollHeight;
          el.style.maxHeight = `${defaultHeight}px`;

          setShowMoreVisible((prev) => ({
            ...prev,
            [category]: actualHeight > defaultHeight,
          }));
        }
      });
    }, 100);
  }, [notes, categories]);

  if (!notes || notes.length === 0) {
    return (
      <div className="card shadow-none">
        <div
          className="card-body d-flex justify-content-center align-items-center"
          style={{ minHeight: "120px" }}
        >
          <p className="text-muted mb-0 text-center">
            {isCasePaperMode ? "No notes available in case paper." : "No notes found."}
          </p>
        </div>
      </div>
    );
  }

  const toggleExpand = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <div className="card shadow-none">
      <div className="card-body" style={{ marginLeft: "-30px" }}>
        <div className="row">
          {Object.keys(categories).map((category) => {
            // Filter notes for this category
            const categoryNotes = notes.filter((note) => {
              // Check if note belongs to this category
              if (note.category_details && note.category_details.category_text === category) {
                return true;
              }
              // Check by note_category ID
              const categoryId = categories[category].id;
              if (categoryId && Number(note.note_category) === categoryId) {
                return true;
              }
              // For "General Notes" category, include notes without category
              if (category === "General Notes" && (!note.note_category || note.note_category === "0" || note.note_category === null)) {
                return true;
              }
              return false;
            });

            if (categoryNotes.length === 0) return null;

            return (
              <div key={category} className="col-md-4 mb-4">
                <div
                  className="border rounded-3 p-3 shadow-sm"
                  style={{
                    backgroundColor: categories[category].bg,
                    height: "260px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* HEADER */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6
                      className="fw-bold mb-0"
                      style={{ color: categories[category].textColor }}
                    >
                      {category}
                    </h6>

                    {/* Only show add button when NOT in case paper mode */}
                    {!isCasePaperMode && (
                      <Button
                        variant={categories[category].btn}
                        size="sm"
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "28px",
                          height: "28px",
                          padding: "0",
                          fontSize: "18px",
                          lineHeight: "1",
                          flexShrink: 0,
                        }}
                        onClick={() => {
                          setDefaultCategory({
                            id: categories[category].id,
                            name: category,
                          });
                          setShowAddNoteModal(true);
                        }}
                      >
                        +
                      </Button>
                    )}
                  </div>

                  {/* SCROLLABLE NOTES */}
                  <div
                    ref={(el) => (containerRefs.current[category] = el)}
                    className="mt-2"
                    style={{
                      flexGrow: 1,
                      maxHeight: expandedCategories[category]
                        ? "100%"
                        : `${defaultHeight}px`,
                      overflowY: expandedCategories[category]
                        ? "auto"
                        : "hidden",
                      overflowX: "hidden",
                      paddingRight: "4px",
                      transition: "max-height 0.3s ease",
                    }}
                  >
                    {categoryNotes.map((note) => (
                      <div
                        key={note.note_id}
                        className="p-2 mb-2 border rounded"
                        style={{ backgroundColor: "rgba(255,255,255,0.7)" }}
                      >
                        <small
                          className="text-muted d-block mb-1"
                          style={{ fontSize: "12px" }}
                        >
                          {formatDate(note.created_at)}
                        </small>

                        <p className="mb-0 fs-15 text-muted">
                          {note.note_description}
                        </p>
                        
                        {/* Show note type if available */}
                        {note.note_type && note.note_type !== "" && (
                          <small className="text-muted d-block mt-1">
                            Type: {note.note_type}
                          </small>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* SHOW MORE / SHOW LESS */}
                  {showMoreVisible[category] && (
                    <button
                      className="btn btn-link p-0 mt-1"
                      style={{ fontSize: "14px" }}
                      onClick={() => toggleExpand(category)}
                    >
                      {expandedCategories[category]
                        ? "▲ Show Less"
                        : "▼ Show More"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Only show AddNotes modal when NOT in case paper mode */}
      {!isCasePaperMode && showAddNoteModal && (
        <AddNotes
          showModal={showAddNoteModal}
          handleClose={() => setShowAddNoteModal(false)}
          defaultCategory={defaultCategory}
        />
      )}
    </div>
  );
};

NotesHistory.propTypes = {
  casePaperData: PropTypes.object,
};

NotesHistory.defaultProps = {
  casePaperData: null,
};